import { Alert, Box, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import hljs from 'highlight.js/lib/core';
import json from 'highlight.js/lib/languages/json';
import type { MouseEvent } from 'react';
import React, { PropsWithChildren, ReactElement, ReactNode, Ref, RefObject, useEffect, useMemo, useRef } from 'react';
import { BreakpointSize } from './hooks/breakpoints';
import { useShallowMergeSxProps } from './hooks/useShallowMergeSxProps';
import { useConfigDebugMode } from './utils/atoms/useConfigDebugMode';

hljs.registerLanguage('json', json);

export type Layouts = {
    [key in BreakpointSize]?: ReactElement;
};

export type BaseLayoutProps = PropsWithChildren<{
    /**
     * HTML element ID
     */
    id?: string;
    /**
     * React ref for accessing the underlying DOM element
     */
    ref?: RefObject<HTMLElement>;
    /**
     * Display children in vertical column layout instead of horizontal row
     * @default false
     */
    isVertical?: boolean;
    /**
     * Stretch layout to fill 100% of parent height
     * @default false
     */
    fill?: boolean;
    /**
     * Allow layout to grow and take up available space (sets flexGrow: 2)
     * @default false
     */
    grow?: boolean;
    /**
     * Conditionally hide the entire layout
     * @default false
     */
    hide?: boolean;
    /**
     * MUI sx prop for additional styling
     * @default {}
     */
    sx?: SxProps<Theme>;
    /**
     * Responsive layout configurations per breakpoint
     */
    layouts?: Layouts[];
    /**
     * Additional CSS class names
     */
    className?: string;
    /**
     * Gap spacing between child elements (in theme spacing units)
     * @default 0
     */
    gap?: number;
    /**
     * Mouse enter event handler for the layout container
     * @param event Mouse event for HTMLDivElement
     */
    onMouseEnter?: (event: MouseEvent<HTMLDivElement>) => void;
    /**
     * Mouse leave event handler for the layout container
     * @param event Mouse event for HTMLDivElement
     */
    onMouseLeave?: (event: MouseEvent<HTMLDivElement>) => void;
    /**
     * Click event handler for the layout container.
     */
    onClick?: (event: MouseEvent<HTMLDivElement>) => void;
    /**
     * Additional props to be spread onto the layout container.
     */
    passthroughprops?: { [key: string]: any };
}>;

export const BaseLayout = ({
    children,
    id,
    ref,
    isVertical = false,
    fill = false,
    grow = false,
    hide = false,
    sx = {},
    layouts,
    className,
    gap = 0,
    ...rest
}: BaseLayoutProps) => {
    const defaultSx: SxProps = useMemo(
        () => ({
            width: '100%',
            height: fill ? '100%' : 'unset',
            display: 'flex',
            gap,
            alignItems: isVertical ? 'flex-start' : 'center',
            alignContent: 'center',
            flexDirection: isVertical ? 'column' : 'row',
            flexGrow: grow ? 2 : 0,
            flexWrap: 'nowrap'
        }),
        [isVertical, fill, grow, gap]
    );
    const containerSx = useShallowMergeSxProps(defaultSx, sx);
    const computedClassName = (isVertical ? '_v' : '_h') + (className ? ` ${className}` : '');

    return (
        !hide && (
            <Box ref={ref} className={computedClassName} sx={containerSx} {...rest}>
                {children}
            </Box>
        )
    );
};

export const V = ({ children, ref, ...rest }: Omit<BaseLayoutProps, 'isVertical'> & { ref?: Ref<HTMLDivElement> }) => {
    return (
        <BaseLayout ref={ref} isVertical={true} {...rest}>
            {children}
        </BaseLayout>
    );
};

export const H = ({ children, ...rest }: Omit<BaseLayoutProps, 'isVertical'>) => {
    return (
        <BaseLayout isVertical={false} {...rest}>
            {children}
        </BaseLayout>
    );
};

export const C = ({ children = undefined, grow = false, hide = false, className = undefined, ...rest }) => {
    const containerCss = {
        flexGrow: grow ? 2 : 0
        // overflow: 'hidden'
    };
    return (
        !hide && (
            <Box sx={containerCss} {...rest} className={`_c${className ? ' ' + className : ''}`}>
                {children}
            </Box>
        )
    );
};

export const Conditional = ({ condition, children }) => {
    if (condition) {
        return typeof children === 'function' ? children() : children;
    } else {
        return <></>;
    }
};

const StatePreview = ({ preview = undefined }) => {
    const editorRef = useRef(null);

    useEffect(() => {
        if (editorRef && editorRef.current) {
            editorRef.current.getAction('editor.action.formatDocument').run();
        }
    }, [preview]);
    const highlightedCode = hljs.highlight(JSON.stringify(preview, null, 2), { language: 'json' }).value;
    return (
        <V sx={{ height: '100%', p: 1 }}>
            <H sx={{ width: '100%' }}>
                <Alert sx={{ width: '100%' }} severity={'info'} variant={'standard'}>
                    State preview
                </Alert>
            </H>
            <pre style={{ overflow: 'auto', width: '100%', margin: 0 }}>
                <code className="language-json" dangerouslySetInnerHTML={{ __html: highlightedCode }}></code>
            </pre>
        </V>
    );
};

type ScrollableLayoutProps = {
    preview?: any;
    sx?: SxProps;
    height?: string;
    horizontal?: boolean;
    layouts?: Layouts[];
    children?: ReactNode;
};

export const ScrollableLayout = (props: ScrollableLayoutProps) => {
    const {
        preview = undefined,
        sx = {},
        height = '100%',
        horizontal = false,
        children = undefined,
        layouts = undefined
    } = props;
    const [isDebug] = useConfigDebugMode();

    const outer = { overflow: 'hidden', height: '100%', flexGrow: 2 };
    const inner = useShallowMergeSxProps(
        {
            overflowX: 'hidden',
            overflowY: 'auto',
            height: '100%',
            gap: 1,
            p: 1,
            flexGrow: 2
        },
        sx
    );
    const Container = horizontal ? H : V;

    return (
        <>
            <Container
                className={'scrollable-layout-outer'}
                sx={{ ...outer, flexGrow: 2, height: height, alignItems: 'flex-start' }}
            >
                <Container className={'scrollable-layout-inner'} sx={inner} layouts={layouts}>
                    {children}
                </Container>
            </Container>
            {isDebug && preview && (
                <V sx={{ ...outer, width: '400px' }}>
                    <StatePreview preview={preview} />
                </V>
            )}
        </>
    );
};
